import { datadogRum } from '@datadog/browser-rum';
import { ddLog, tabId, DD_LOG_KEY, getSessionId } from './log-store.js';
import { initJiraForm } from './jira-form.js';
import { initAiTab } from './ai-tab.js';

const TYPE_STYLE = {
  click: { bg: '#e8f0fe', color: '#1a56db', icon: '🖱' },
  input: { bg: '#fef3c7', color: '#92400e', icon: '⌨' },
  nav: { bg: '#d1fae5', color: '#065f46', icon: '🔀' },
};

function buildUI(DATADOG_SITE) {
  const shadow = document.createElement('div');
  shadow.id = '__dd-qa-root';
  Object.assign(shadow.style, { all: 'initial', position: 'fixed', zIndex: 999 });
  document.body.appendChild(shadow);

  const root = shadow.attachShadow({ mode: 'open' });

  root.innerHTML = `
    <style>
      * { box-sizing: border-box; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
      #toggle-btn {
        position: fixed; bottom: 4px; right: 4px;
        background: #1e1b4b; color: #fff; border: none; border-radius: 50%;
        width: 28px; height: 28px; font-size: 14px; cursor: pointer;
        box-shadow: 0 2px 8px rgba(0,0,0,.3);
        display: flex; align-items: center; justify-content: center; padding: 0;
      }
      #toggle-btn:hover { background: #312e81; }
      #overlay {
        display: none; position: fixed; inset: 0;
        background: rgba(0,0,0,.45); backdrop-filter: blur(2px);
      }
      #modal {
        position: fixed; top: 50%; left: 50%; transform: translate(-50%,-50%);
        width: min(1100px, 95vw); max-height: 80vh;
        background: #fff; border-radius: 12px; box-shadow: 0 20px 60px rgba(0,0,0,.35);
        display: flex; flex-direction: column;
      }
      #body-row { display: flex; flex: 1; overflow: hidden; min-height: 0; border-radius: 0 0 12px 12px; }
      #header {
        display: flex; align-items: center; justify-content: space-between;
        padding: 14px 18px; background: #1e1b4b; color: #fff;
        border-radius: 12px 12px 0 0; flex-shrink: 0;
      }
      #header h2 { margin: 0; font-size: 15px; font-weight: 700; }
      #header-actions { display: flex; gap: 8px; }
      .hdr-btn {
        background: rgba(255,255,255,.15); color: #fff; border: none;
        border-radius: 6px; padding: 5px 10px; font-size: 12px; cursor: pointer;
      }
      .hdr-btn:hover { background: rgba(255,255,255,.25); }
      #list { overflow-y: auto; flex: 1; padding: 10px; display: flex; flex-direction: column; gap: 4px; min-width: 0; }
      .entry {
        display: flex; align-items: flex-start; gap: 8px;
        border-radius: 6px; padding: 7px 10px; font-size: 12px; cursor: pointer;
      }
      .entry .icon { font-size: 14px; flex-shrink: 0; margin-top: 1px; }
      .entry .meta { color: #6b7280; font-size: 11px; margin-top: 2px; }
      .entry .num { color: #9ca3af; font-size: 11px; flex-shrink: 0; margin-top: 2px; width: 20px; }
      .entry input[type=checkbox] { width: 14px; height: 14px; flex-shrink: 0; margin-top: 2px; cursor: pointer; accent-color: #1e1b4b; }
      .entry.selected { outline: 2px solid #6366f1; outline-offset: -2px; }
      #sel-bar { display:none; padding: 6px 18px; background: #f5f3ff; border-bottom: 1px solid #e5e7eb; font-size: 12px; color: #4b5563; align-items: center; gap: 10px; }
      #sel-bar.visible { display: flex; }
      #sel-all-btn { background: none; border: 1px solid #6366f1; color: #4f46e5; border-radius: 4px; padding: 3px 8px; font-size: 11px; cursor: pointer; }
      #info-btn { position: relative; background: rgba(255,255,255,.15); color: #fff; border: none; border-radius: 50%; width: 22px; height: 22px; font-size: 12px; cursor: default; display: flex; align-items: center; justify-content: center; }
      #info-tooltip {
        display: none; position: absolute; right: 0; top: 28px;
        background: #1e1b4b; color: #e0e7ff; border-radius: 8px;
        padding: 12px 14px; width: 280px; font-size: 11px; line-height: 1.6;
        box-shadow: 0 8px 24px rgba(0,0,0,.4); z-index: 999; white-space: normal;
        border: 1px solid rgba(255,255,255,.15);
      }
      #info-btn:hover #info-tooltip { display: block; }
      #info-tooltip b { color: #a5b4fc; }
      #info-tooltip hr { border: none; border-top: 1px solid rgba(255,255,255,.1); margin: 8px 0; }
      #empty { color: #9ca3af; font-size: 13px; text-align: center; padding: 40px; }
      #storage-warn { display:none; padding: 7px 18px; background: #fff7ed; border-bottom: 1px solid #fed7aa; font-size: 12px; color: #9a3412; align-items: center; gap: 6px; }
      #storage-warn.visible { display: flex; }
      #storage-warn span { flex:1; }
      #storage-warn progress { width: 80px; height: 8px; accent-color: #ea580c; }
      #view-tabs { display: flex; border-bottom: 2px solid #e5e7eb; flex-shrink: 0; background: #fff; }
      .view-tab { background: none; border: none; border-bottom: 2px solid transparent; margin-bottom: -2px; padding: 8px 16px; font-size: 12px; font-weight: 600; color: #6b7280; cursor: pointer; font-family: inherit; }
      .view-tab:hover { color: #1e1b4b; }
      .view-tab.active { color: #1e1b4b; border-bottom-color: #6366f1; }
    </style>
    <button id="toggle-btn" title="Show Logs (tab id: ${tabId})" aria-label="Show Logs">≡</button>
    <div id="overlay"></div>
    <div id="modal" style="display:none">
      <div id="header">
        <h2 style="margin:0;font-size:15px;font-weight:700;display:flex;flex-direction:column;gap:2px">
          <span>QA Logs (<span id="count">0</span>)</span>
          <span style="font-size:10px;font-weight:400;opacity:.45;letter-spacing:.5px">
            TAB&nbsp;${tabId}
          </span>
          <span style="font-size:10px;font-weight:400;opacity:.45;letter-spacing:.5px">
            SID&nbsp;<span id="session-display">${getSessionId() ?? '—'}</span>
          </span>
        </h2>
        <div id="header-actions">
          <div id="info-btn">ℹ
            <div id="info-tooltip">
              <b>Selection</b><hr/>
              <b>Click</b> — add to selection<br/>
              <b>Shift+Click</b> — select range from anchor<br/>
              <b>Ctrl/Cmd+Click</b> — toggle individual<br/>
              <hr/>
              <b>Filters</b><hr/>
              <b>This Tab / All Tabs</b> — show only current tab's logs or all tabs<br/>
              <hr/>
              <b>Copy</b> — copies selected rows (or all if none selected)<br/>
              <b>Console:</b> <code style="color:#a5b4fc">__printDDLog()</code>
            </div>
          </div>
          <button class="hdr-btn" id="tab-btn" style="background:rgba(255,255,255,.4)">This Tab</button>
          <a class="hdr-btn" id="replay-link" href="#" target="_blank" rel="noopener" style="display:none;text-decoration:none">▶ Replay</a>
          <button class="hdr-btn" id="copy-btn">Copy (<span id="copy-count">all</span>)</button>
          <button class="hdr-btn" id="clear-btn">Clear</button>
          <button class="hdr-btn" id="close-btn">✕</button>
        </div>
      </div>
      <div id="storage-warn"><span id="storage-warn-text"></span><progress id="storage-progress" max="100" value="0"></progress></div>
      <div id="sel-bar"><span id="sel-label">0 selected</span><button id="sel-all-btn">Select all</button><button id="sel-none-btn" style="background:none;border:none;color:#9ca3af;font-size:11px;cursor:pointer">Clear</button></div>
      <div id="view-tabs">
        <button class="view-tab active" data-view="logs">Logs</button>
        <button class="view-tab" data-view="ai">✨ AI Analyze</button>
      </div>
      <div id="body-row"><div id="list"><div id="empty">No events captured yet.</div></div></div>
    </div>
  `;

  const toggleBtn = root.getElementById('toggle-btn');
  const overlay = root.getElementById('overlay');
  const modal = root.getElementById('modal');
  const list = root.getElementById('list');
  const countEl = root.getElementById('count');
  const empty = root.getElementById('empty');
  const selBar = root.getElementById('sel-bar');
  const selLabel = root.getElementById('sel-label');
  const copyCount = root.getElementById('copy-count');
  const storageWarn = root.getElementById('storage-warn');
  const storageWarnText = root.getElementById('storage-warn-text');
  const storageProgress = root.getElementById('storage-progress');

  const LS_MAX_BYTES = 5 * 1024 * 1024; // 5 MB typical limit
  const LS_WARN_RATIO = 0.7; // warn at 70%
  function updateStorageWarn() {
    try {
      const used = Object.keys(localStorage).reduce((sum, k) => sum + (localStorage.getItem(k) || '').length * 2, 0);
      const pct = used / LS_MAX_BYTES;
      storageProgress.value = Math.round(pct * 100);
      if (pct >= LS_WARN_RATIO) {
        const usedMB = (used / 1024 / 1024).toFixed(1);
        storageWarnText.textContent = `⚠ localStorage ${Math.round(pct * 100)}% full (${usedMB} MB of ~5 MB) — consider clearing logs`;
        storageWarn.classList.add('visible');
      } else {
        storageWarn.classList.remove('visible');
      }
    } catch { /* non-critical */ }
  }

  let thisTabOnly = true;
  let selected = new Set();
  let anchor = null;
  let jiraForm = null;

  function updateSelUI() {
    const n = selected.size;
    selBar.classList.toggle('visible', n > 0);
    selLabel.textContent = `${n} selected`;
    copyCount.textContent = n > 0 ? n : 'all';
    root.querySelectorAll('.entry[data-id]').forEach(el => {
      const entryId = Number(el.dataset.id);
      el.classList.toggle('selected', selected.has(entryId));
      el.querySelector('input[type=checkbox]').checked = selected.has(entryId);
    });
    updateReplayLink();
    jiraForm?.refreshPreview();
  }

  // Re-check storage when another tab writes
  window.addEventListener('storage', (ev) => {
    if (ev.key === DD_LOG_KEY) updateStorageWarn();
  });

  function open() {
    shadow.style.zIndex = 2147483647;
    updateStorageWarn();
    updateReplayLink();
    renderList();
    overlay.style.display = 'block';
    modal.style.display = 'flex';
    toggleBtn.textContent = '✕';
    toggleBtn.title = `Hide Logs (tab id: ${tabId})`;
    toggleBtn.setAttribute('aria-label', 'Hide Logs');
    requestAnimationFrame(() => { list.scrollTop = list.scrollHeight; });
  }
  function close() {
    shadow.style.zIndex = 999;
    overlay.style.display = 'none';
    modal.style.display = 'none';
    toggleBtn.textContent = '≡'; toggleBtn.title = `Show Logs (tab id: ${tabId})`;
    toggleBtn.setAttribute('aria-label', 'Show Logs'); }

  function getVisible() {
    let entries = ddLog;
    if (thisTabOnly) entries = entries.filter(e => e.tab === tabId);
    return entries;
  }

  function renderList() {
    const visible = getVisible();
    countEl.textContent = visible.length;
    if (!visible.length) { list.innerHTML = ''; list.appendChild(empty); return; }

    list.innerHTML = visible.map((e, i) => {
      const s = TYPE_STYLE[e.type] || { bg: '#f3f4f6', color: '#374151', icon: '•' };
      const tabBadge = e.tab && e.tab !== tabId
        ? `<span style="font-size:9px;font-weight:600;opacity:.7;background:rgba(0,0,0,.12);border-radius:3px;padding:1px 4px;margin-left:5px;letter-spacing:.4px;vertical-align:middle">TAB ${e.tab}</span>`
        : '';
      const testIdBadge = e.testId
        ? `<span style="font-size:9px;font-weight:600;background:rgba(0,0,0,.08);border-radius:3px;padding:1px 5px;margin-left:5px;letter-spacing:.3px;vertical-align:middle;font-family:monospace">test-id: ${e.testId}</span>`
        : '';
      return `
        <div class="entry" style="background:${s.bg};color:${s.color}" data-i="${i}" data-id="${e.id}">
          <input type="checkbox" data-cb="${i}" />
          <span class="num">${i + 1}</span>
          <span class="icon">${s.icon}</span>
          <div style="flex:1;min-width:0">
            <div style="font-weight:600;word-break:break-all">${e.label}${tabBadge}${testIdBadge}</div>
            <div class="meta">${e.t.replace('T', ' ').replace('Z', '')}</div>
          </div>
        </div>`;
    }).join('');

    list.querySelectorAll('.entry[data-id]').forEach(el => {
      const handleSelect = (ev) => {
        const entryId = Number(el.dataset.id);
        if (ev.shiftKey && anchor !== null) {
          const vis = getVisible();
          const anchorIdx = vis.findIndex(e => e.id === anchor);
          const curIdx = vis.findIndex(e => e.id === entryId);
          if (anchorIdx !== -1 && curIdx !== -1) {
            const min = Math.min(anchorIdx, curIdx);
            const max = Math.max(anchorIdx, curIdx);
            vis.slice(min, max + 1).forEach(e => selected.add(e.id));
          }
        } else {
          selected.has(entryId) ? selected.delete(entryId) : selected.add(entryId);
          anchor = entryId;
        }
        updateSelUI();
      };
      el.querySelector('input[type=checkbox]').addEventListener('click', (ev) => {
        ev.stopPropagation();
        handleSelect(ev);
      });
      el.addEventListener('click', (ev) => handleSelect(ev));
    });
    updateSelUI();
  }

  toggleBtn.addEventListener('click', () => modal.style.display === 'none' ? open() : close());
  overlay.addEventListener('click', close);
  root.getElementById('close-btn').addEventListener('click', close);
  root.getElementById('sel-all-btn').addEventListener('click', () => {
    getVisible().forEach(e => selected.add(e.id));
    updateSelUI();
  });
  root.getElementById('sel-none-btn').addEventListener('click', () => { selected.clear(); anchor = null; updateSelUI(); });
  root.getElementById('clear-btn').addEventListener('click', () => {
    ddLog.length = 0;
    localStorage.removeItem(DD_LOG_KEY);
    selected.clear();
    anchor = null;
    updateStorageWarn();
    renderList();
  });

  const tabBtn = root.getElementById('tab-btn');
  const replayLink = root.getElementById('replay-link');

  function buildReplayUrl(firstSelected) {
    const ctx = datadogRum.getInternalContext?.();
    if (!ctx?.session_id) return null;
    const params = new URLSearchParams();
    if (ctx.application_id) params.set('applicationId', ctx.application_id);
    if (firstSelected?.t) {
      const ts = new Date(firstSelected.t).getTime();
      if (!Number.isNaN(ts)) params.set('from', String(ts));
    }
    return `https://${DATADOG_SITE}/rum/replay/sessions/${ctx.session_id}?${params}`;
  }

  function updateReplayLink() {
    try {
      const visible = getVisible();
      const firstSelected = selected.size > 0 ? visible.find(e => selected.has(e.id)) : null;
      const url = datadogRum.getSessionReplayLink?.() ?? buildReplayUrl(firstSelected);
      if (url) { replayLink.href = url; replayLink.style.display = ''; }
      else { replayLink.style.display = 'none'; }
    } catch { replayLink.style.display = 'none'; }
  }

  tabBtn.addEventListener('click', () => {
    thisTabOnly = !thisTabOnly;
    tabBtn.textContent = thisTabOnly ? 'This Tab' : 'All Tabs';
    tabBtn.style.background = thisTabOnly ? 'rgba(255,255,255,.4)' : '';
    renderList();
  });

  root.getElementById('copy-btn').addEventListener('click', () => {
    const visible = getVisible();
    const source = selected.size > 0 ? visible.filter(e => selected.has(e.id)) : visible;
    const text = source.map((e, i) => `${i + 1}. [${e.t}] ${e.label}`).join('\n');
    const header = `Tab: ${tabId}\n${'─'.repeat(30)}\n`;
    navigator.clipboard.writeText(header + text).then(() => {
      const btn = root.getElementById('copy-btn');
      btn.innerHTML = 'Copied! (<span id="copy-count">' + copyCount.textContent + '</span>)';
      setTimeout(() => { btn.innerHTML = 'Copy (<span id="copy-count">' + (selected.size || 'all') + '</span>)'; }, 2000);
    });
  });

  jiraForm = initJiraForm({
    root,
    getSelectedEntries: () => getVisible().filter(e => selected.has(e.id)),
    getReplayUrl: (firstSelected) => buildReplayUrl(firstSelected),
    clearSelection: () => { selected.clear(); anchor = null; updateSelUI(); },
  });

  const aiTab = initAiTab({ root, getParentKey: () => jiraForm.getParentKey() });

  const bodyRow = root.getElementById('body-row');
  const storageWarnEl = root.getElementById('storage-warn');
  const selBarEl = root.getElementById('sel-bar');

  root.getElementById('view-tabs').querySelectorAll('.view-tab').forEach(btn => {
    btn.addEventListener('click', () => {
      root.getElementById('view-tabs').querySelectorAll('.view-tab').forEach(b => b.classList.toggle('active', b === btn));
      const view = btn.dataset.view;
      if (view === 'ai') {
        bodyRow.style.display = 'none';
        storageWarnEl.style.display = 'none';
        selBarEl.style.display = 'none';
        aiTab.activate();
      } else {
        bodyRow.style.display = '';
        storageWarnEl.style.display = '';
        selBarEl.style.display = '';
        aiTab.deactivate();
      }
    });
  });
}

export function initUI(DATADOG_SITE) {
  document.addEventListener('DOMContentLoaded', () => buildUI(DATADOG_SITE));
}
